import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    KeyRound, LogOut, Plus, Pencil, Trash2, X, Home,
    LayoutGrid, Tag, CircleDollarSign, CircleCheck,
    Newspaper, Tags, Settings2, Star, Share2, Palette,
    ClipboardList,
    UserCog,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { LogoBadge } from "../components/site/LogoBadge";
import { PostsPanel } from "../components/admin/PostsPanel";
import { CategoriesPanel } from "../components/admin/CategoriesPanel";
import { ContentPanel } from "../components/admin/ContentPanel";
import { BrandsPanel } from "../components/admin/BrandsPanel";
import { SocialsPanel } from "../components/admin/SocialsPanel";
import { CustomizePanel } from "../components/admin/CustomizePanel";
import { InquiryPanel } from "../components/admin/InquiryPanel";
import { AccountPanel } from "../components/admin/AccountPanel";

const EMPTY_FORM = {
    brand: "", brand_ar: "", model: "", size: "", rim: "R16",
    category: "sedan", category_ar: "سيدان وعائلي", speed_rating: "",
    origin: "", price: "", old_price: "", in_stock: true, year: "2024",
    image: "", features: "",
};

const CATEGORY_MAP = {
    sedan: "سيدان وعائلي",
    suv: "دفع رباعي / SUV",
    performance: "رياضي وعالي الأداء",
    commercial: "تجاري ونقل خفيف",
};

const ADMIN_TABS = [
    { key: "tires", label: "الإطارات", icon: LayoutGrid, testid: "admin-tab-tires" },
    { key: "brands", label: "الماركات", icon: Star, testid: "admin-tab-brands" },
    { key: "socials", label: "التواصل الاجتماعي", icon: Share2, testid: "admin-tab-socials" },
    { key: "posts", label: "المقالات", icon: Newspaper, testid: "admin-tab-posts" },
    { key: "categories", label: "التصنيفات", icon: Tags, testid: "admin-tab-categories" },
    { key: "content", label: "محتوى الموقع", icon: Settings2, testid: "admin-tab-content" },
    { key: "customize", label: "تخصيص الموقع", icon: Palette, testid: "admin-tab-customize" },
    { key: "inquiry", label: "نموذج الاستفسار", icon: ClipboardList, testid: "admin-tab-inquiry" },
    { key: "account", label: "إعدادات الحساب", icon: UserCog, testid: "admin-tab-account" },
];

function LoginForm({ onSuccess }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem("thuraya_admin_token", data.token);
            onSuccess(data.user);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "فشل تسجيل الدخول");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0A1228] flex items-center justify-center px-4 grain-overlay">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="w-full max-w-md spotlight-card border border-white/10 rounded-3xl p-8"
            >
                <div className="flex justify-center mb-6">
                    <LogoBadge />
                </div>
                <h1 className="font-display font-extrabold text-xl text-white text-center mb-1">
                    لوحة تحكم المتجر
                </h1>
                <p className="text-xs text-slate-500 text-center mb-7">
                    منطقة خاصة بإدارة شركة إطار الثريا لخدمات السيارات
                </p>
                <form onSubmit={submit} className="space-y-4">
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="البريد الإلكتروني"
                        data-testid="admin-email-input"
                        className="w-full bg-[#0A1228]/70 border border-white/10 rounded-2xl py-3 px-4 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 transition-colors duration-300"
                    />
                    <input
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="كلمة المرور"
                        data-testid="admin-password-input"
                        className="w-full bg-[#0A1228]/70 border border-white/10 rounded-2xl py-3 px-4 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 transition-colors duration-300"
                    />
                    {error && (
                        <p className="text-xs text-red-400 text-center" data-testid="admin-login-error">
                            {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="admin-login-button"
                        className="w-full flex items-center justify-center gap-2 bg-amber-400 text-[#0A1228] font-extrabold text-sm rounded-2xl py-3.5 hover:bg-amber-300 transition-colors duration-300 disabled:opacity-50"
                    >
                        <KeyRound className="w-4 h-4" />
                        {loading ? "جاري الدخول..." : "تسجيل الدخول"}
                    </button>
                    <Link
                        to="/"
                        className="block text-center text-xs text-slate-500 hover:text-amber-400 transition-colors duration-300"
                    >
                        العودة للموقع الرئيسي
                    </Link>
                </form>
            </motion.div>
        </div>
    );
}

function TireFormModal({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(initial.id);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const payload = {
            ...form,
            price: Number(form.price),
            old_price: form.old_price ? Number(form.old_price) : null,
            category_ar: CATEGORY_MAP[form.category] || form.category_ar,
            features: typeof form.features === "string"
                ? form.features.split("،").map((s) => s.trim()).filter(Boolean)
                : form.features,
        };
        delete payload.id;
        try {
            if (isEdit) {
                await api.put(`/tires/${initial.id}`, payload);
                toast.success("تم تحديث الإطار بنجاح");
            } else {
                await api.post("/tires", payload);
                toast.success("تمت إضافة الإطار بنجاح");
            }
            onSaved();
            onClose();
        } catch {
            toast.error("تعذر الحفظ — تحقق من البيانات");
        } finally {
            setSaving(false);
        }
    };

    const inputCls =
        "w-full bg-[#0A1228]/70 border border-white/10 rounded-xl py-2.5 px-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 transition-colors duration-300";

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                onClick={(e) => e.stopPropagation()}
                data-testid="admin-tire-form-modal"
                className="w-full max-w-2xl max-h-[90vh] overflow-y-auto spotlight-card border border-white/10 rounded-3xl p-6 sm:p-8"
            >
                <div className="flex items-center justify-between mb-6">
                    <h2 className="font-display font-bold text-lg text-white">
                        {isEdit ? "تعديل الإطار" : "إضافة إطار جديد"}
                    </h2>
                    <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="admin-form-close-button">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
                    <input required value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="الماركة (إنجليزي) مثال: Michelin" data-testid="form-brand" className={inputCls} dir="ltr" />
                    <input required value={form.brand_ar} onChange={(e) => set("brand_ar", e.target.value)} placeholder="الماركة (عربي) مثال: ميشلان" data-testid="form-brand-ar" className={inputCls} />
                    <input required value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="الموديل مثال: Pilot Sport 4" data-testid="form-model" className={inputCls} dir="ltr" />
                    <input required value={form.size} onChange={(e) => set("size", e.target.value)} placeholder="المقاس مثال: 265/65 R17" data-testid="form-size" className={inputCls} dir="ltr" />
                    <select value={form.rim} onChange={(e) => set("rim", e.target.value)} data-testid="form-rim" className={inputCls}>
                        {["R15", "R16", "R17", "R18", "R19", "R20", "R21", "R22"].map((r) => (
                            <option key={r} value={r}>{r}</option>
                        ))}
                    </select>
                    <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="form-category" className={inputCls}>
                        {Object.entries(CATEGORY_MAP).map(([v, l]) => (
                            <option key={v} value={v}>{l}</option>
                        ))}
                    </select>
                    <input value={form.speed_rating} onChange={(e) => set("speed_rating", e.target.value)} placeholder="مؤشر السرعة مثال: 98Y" data-testid="form-speed" className={inputCls} dir="ltr" />
                    <input value={form.origin} onChange={(e) => set("origin", e.target.value)} placeholder="بلد المنشأ" data-testid="form-origin" className={inputCls} />
                    <input required type="number" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="السعر (ر.س)" data-testid="form-price" className={inputCls} dir="ltr" />
                    <input type="number" min="0" value={form.old_price ?? ""} onChange={(e) => set("old_price", e.target.value)} placeholder="السعر قبل الخصم (اختياري)" data-testid="form-old-price" className={inputCls} dir="ltr" />
                    <input value={form.year} onChange={(e) => set("year", e.target.value)} placeholder="سنة الإنتاج" data-testid="form-year" className={inputCls} dir="ltr" />
                    <input value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="رابط صورة الإطار" data-testid="form-image" className={inputCls} dir="ltr" />
                    <textarea
                        value={typeof form.features === "string" ? form.features : form.features.join("، ")}
                        onChange={(e) => set("features", e.target.value)}
                        placeholder="المميزات — افصل بينها بفاصلة عربية (،)"
                        data-testid="form-features"
                        className={`${inputCls} sm:col-span-2 h-20 resize-none`}
                    />
                    <label className="sm:col-span-2 flex items-center gap-3 text-sm text-slate-300 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.in_stock}
                            onChange={(e) => set("in_stock", e.target.checked)}
                            data-testid="form-in-stock"
                            className="w-4 h-4 accent-amber-400"
                        />
                        متوفر في المخزون
                    </label>
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="admin-form-submit-button"
                        className="sm:col-span-2 bg-amber-400 text-[#0A1228] font-extrabold text-sm rounded-xl py-3.5 hover:bg-amber-300 transition-colors duration-300 disabled:opacity-50"
                    >
                        {saving ? "جاري الحفظ..." : isEdit ? "حفظ التعديلات" : "إضافة الإطار"}
                    </button>
                </form>
            </motion.div>
        </motion.div>
    );
}

function Dashboard({ user, onLogout, onUserUpdate }) {
    const [tires, setTires] = useState([]);
    const [modal, setModal] = useState(null);
    const [tab, setTab] = useState("tires");

    const load = useCallback(() => {
        api.get("/tires").then((r) => setTires(r.data)).catch(() => {});
    }, []);

    useEffect(() => { load(); }, [load]);

    const stats = {
        total: tires.length,
        brands: new Set(tires.map((t) => t.brand)).size,
        avg: tires.length ? Math.round(tires.reduce((s, t) => s + t.price, 0) / tires.length) : 0,
        active: tires.filter((t) => t.in_stock).length,
    };

    const remove = async (t) => {
        if (!window.confirm(`حذف ${t.brand_ar} ${t.model}؟`)) return;
        try {
            await api.delete(`/tires/${t.id}`);
            toast.success("تم حذف الإطار");
            load();
        } catch {
            toast.error("تعذر الحذف");
        }
    };

    const STAT_CARDS = [
        { icon: LayoutGrid, label: "إجمالي الإطارات", value: stats.total, testid: "stat-total" },
        { icon: Tag, label: "عدد الماركات", value: stats.brands, testid: "stat-brands" },
        { icon: CircleDollarSign, label: "متوسط السعر", value: `${stats.avg} ر.س`, testid: "stat-avg" },
        { icon: CircleCheck, label: "إطارات متوفرة", value: stats.active, testid: "stat-active" },
    ];

    return (
        <div className="min-h-screen bg-[#0A1228] grain-overlay" data-testid="admin-dashboard-container">
            <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A1228]/85 border-b border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <LogoBadge />
                    <div className="flex items-center gap-3">
                        <span className="hidden sm:block text-xs text-slate-500">{user.email}</span>
                        <Link
                            to="/"
                            data-testid="admin-back-home"
                            className="flex items-center gap-1.5 text-xs font-bold text-slate-300 border border-white/15 rounded-full px-4 py-2 hover:border-amber-400/50 transition-colors duration-300"
                        >
                            <Home className="w-3.5 h-3.5" /> الموقع
                        </Link>
                        <button
                            onClick={onLogout}
                            data-testid="admin-logout-button"
                            className="flex items-center gap-1.5 text-xs font-bold text-red-400 border border-red-500/30 rounded-full px-4 py-2 hover:bg-red-500 hover:text-white transition-colors duration-300"
                        >
                            <LogOut className="w-3.5 h-3.5" /> خروج
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 flex gap-2 flex-wrap" data-testid="admin-tabs">
                {ADMIN_TABS.map((tb) => (
                    <button
                        key={tb.key}
                        onClick={() => setTab(tb.key)}
                        data-testid={tb.testid}
                        className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-extrabold border transition-colors duration-300 ${
                            tab === tb.key
                                ? "bg-amber-400 text-[#0A1228] border-amber-400"
                                : "border-white/10 text-slate-400 hover:border-amber-400/40 hover:text-amber-300"
                        }`}
                    >
                        <tb.icon className="w-4 h-4" />
                        {tb.label}
                    </button>
                ))}
            </div>

            {tab !== "tires" && (
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                    {tab === "posts" && <PostsPanel />}
                    {tab === "categories" && <CategoriesPanel />}
                    {tab === "brands" && <BrandsPanel />}
                    {tab === "socials" && <SocialsPanel />}
                    {tab === "content" && <ContentPanel />}
                    {tab === "customize" && <CustomizePanel />}
                    {tab === "inquiry" && <InquiryPanel />}
                    {tab === "account" && <AccountPanel user={user} onUserUpdate={onUserUpdate} />}
                </main>
            )}

            {tab === "tires" && (
            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-display font-extrabold text-2xl text-white">إدارة مخزون الإطارات</h1>
                        <p className="text-sm text-slate-500 mt-1">أضف وعدّل واحذف الإطارات والأسعار مباشرة</p>
                    </div>
                    <button
                        onClick={() => setModal({ ...EMPTY_FORM })}
                        data-testid="admin-add-tire-button"
                        className="flex items-center gap-2 bg-amber-400 text-[#0A1228] font-extrabold text-sm rounded-full px-6 py-3 hover:bg-amber-300 transition-colors duration-300"
                    >
                        <Plus className="w-4 h-4" /> إضافة إطار جديد
                    </button>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {STAT_CARDS.map((s) => (
                        <div key={s.label} className="spotlight-card border border-white/10 rounded-2xl p-5" data-testid={s.testid}>
                            <s.icon className="w-5 h-5 text-amber-400 mb-2" />
                            <p className="font-display font-extrabold text-2xl text-white">{s.value}</p>
                            <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                        </div>
                    ))}
                </div>

                <div className="border border-white/10 rounded-3xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[760px]">
                            <thead>
                                <tr className="bg-[#101B3D] text-slate-400 text-xs">
                                    <th className="text-right py-3.5 px-5 font-bold">الإطار</th>
                                    <th className="text-right py-3.5 px-5 font-bold">المقاس</th>
                                    <th className="text-right py-3.5 px-5 font-bold">الفئة</th>
                                    <th className="text-right py-3.5 px-5 font-bold">السعر</th>
                                    <th className="text-right py-3.5 px-5 font-bold">الحالة</th>
                                    <th className="text-right py-3.5 px-5 font-bold">إجراءات</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tires.map((t) => (
                                    <tr key={t.id} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors duration-200" data-testid={`admin-tire-row-${t.id}`}>
                                        <td className="py-3.5 px-5">
                                            <p className="font-bold text-white">{t.brand_ar} {t.model}</p>
                                            <p className="text-[11px] text-slate-600" dir="ltr">{t.brand}</p>
                                        </td>
                                        <td className="py-3.5 px-5 font-mono-spec text-amber-400 text-xs" dir="ltr">{t.size}</td>
                                        <td className="py-3.5 px-5 text-slate-400 text-xs">{t.category_ar}</td>
                                        <td className="py-3.5 px-5 font-bold text-white">{t.price} ر.س</td>
                                        <td className="py-3.5 px-5">
                                            {t.in_stock ? (
                                                <span className="text-[11px] font-bold text-emerald-400">متوفر</span>
                                            ) : (
                                                <span className="text-[11px] font-bold text-red-400">نفد</span>
                                            )}
                                        </td>
                                        <td className="py-3.5 px-5">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => setModal({ ...t, old_price: t.old_price ?? "" })}
                                                    data-testid={`admin-edit-tire-${t.id}`}
                                                    className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-amber-400 hover:border-amber-400/50 transition-colors duration-200"
                                                    aria-label="تعديل"
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => remove(t)}
                                                    data-testid={`admin-delete-tire-${t.id}`}
                                                    className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-500/50 transition-colors duration-200"
                                                    aria-label="حذف"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
            )}

            <AnimatePresence>
                {modal && (
                    <TireFormModal initial={modal} onClose={() => setModal(null)} onSaved={load} />
                )}
            </AnimatePresence>
        </div>
    );
}

const IDLE_LOGOUT_MINUTES = 30;

export default function AdminPage() {
    const [user, setUser] = useState(null);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        if (!user) return;
        let timer;
        const logout = () => {
            localStorage.removeItem("thuraya_admin_token");
            setUser(null);
            toast.info("تم تسجيل الخروج تلقائياً بعد فترة خمول");
        };
        const reset = () => {
            clearTimeout(timer);
            timer = setTimeout(logout, IDLE_LOGOUT_MINUTES * 60 * 1000);
        };
        const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
        events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
        reset();
        return () => {
            clearTimeout(timer);
            events.forEach((e) => window.removeEventListener(e, reset));
        };
    }, [user]);

    useEffect(() => {
        const token = localStorage.getItem("thuraya_admin_token");
        if (!token) {
            setChecking(false);
            return;
        }
        api.get("/auth/me")
            .then((r) => setUser(r.data))
            .catch(() => localStorage.removeItem("thuraya_admin_token"))
            .finally(() => setChecking(false));
    }, []);

    if (checking) {
        return (
            <div className="min-h-screen bg-[#0A1228] flex items-center justify-center text-slate-500 text-sm">
                جاري التحقق...
            </div>
        );
    }

    if (!user) return <LoginForm onSuccess={setUser} />;

    return (
        <Dashboard
            user={user}
            onUserUpdate={setUser}
            onLogout={() => {
                localStorage.removeItem("thuraya_admin_token");
                setUser(null);
            }}
        />
    );
}
